import { useEffect, useRef } from "react";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { formatPrice } from "../data/products";

const resolveImg = (src) =>
  !src ? "" : src.startsWith("http") ? src : `${process.env.REACT_APP_BACKEND_URL}${src}`;

/**
 * Marquee horizontal auto-scroll. Se mueve solo y responde al hover en los bordes.
 * Ahora con flechas laterales para saltar manualmente.
 */
export default function ProductMarquee({ title, eyebrow, description, items, onSelect, ctaLabel = "Cotiza", priceTag, testId }) {
  const trackRef = useRef(null);
  const rafRef = useRef(null);
  const speedRef = useRef(0.6);
  const directionRef = useRef(-1);

  const loop = () => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollLeft += speedRef.current * directionRef.current;
    const max = el.scrollWidth - el.clientWidth;
    if (el.scrollLeft <= 0) el.scrollLeft = max / 2;
    if (el.scrollLeft >= max) el.scrollLeft = max / 2;
    rafRef.current = requestAnimationFrame(loop);
  };

  const start = () => {
    if (rafRef.current) return;
    loop();
  };
  const stop = () => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  };

  useEffect(() => {
    const el = trackRef.current;
    if (el) el.scrollLeft = el.scrollWidth / 4;
    start();
    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMouseMove = (e) => {
    const el = trackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = x / rect.width;
    if (ratio < 0.25) {
      directionRef.current = -1;
      speedRef.current = 3.5;
    } else if (ratio > 0.75) {
      directionRef.current = 1;
      speedRef.current = 3.5;
    } else {
      directionRef.current = -1;
      speedRef.current = 0.6;
    }
  };

  const jump = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 600, behavior: "smooth" });
  };

  if (!items || items.length === 0) return null;
  const loopItems = [...items, ...items];

  return (
    <section className="py-20 md:py-28 border-b border-[#EAEAEA]" data-testid={testId}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div className="max-w-2xl space-y-3">
          {eyebrow && <span className="eyebrow">{eyebrow}</span>}
          <h2 className="font-serif-display text-3xl md:text-5xl text-[#1C1C1C] leading-tight tracking-tight">
            {title}
          </h2>
          {description && (
            <p className="text-[#595959] leading-relaxed">{description}</p>
          )}
        </div>
        {priceTag && (
          <div className="flex flex-col items-start md:items-end">
            <span className="eyebrow text-[#B8933A]">Todos a un solo precio</span>
            <span className="font-serif-display text-4xl md:text-5xl text-[#1C1C1C] font-semibold">
              {priceTag}
            </span>
          </div>
        )}
      </div>

      <div className="relative">
        {/* flecha izquierda */}
        <button
          type="button"
          onClick={() => jump(-1)}
          aria-label="Anterior"
          className="flex absolute left-1 md:left-2 top-1/2 -translate-y-1/2 z-10 w-9 h-9 md:w-11 md:h-11 items-center justify-center bg-[#1C1C1C]/90 text-[#E9C46A] border border-[#B8933A] hover:bg-[#B8933A] hover:text-white active:bg-[#B8933A] transition-colors shadow-lg"
          data-testid={`${testId}-prev`}
        >
          <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
        </button>

        {/* flecha derecha */}
        <button
          type="button"
          onClick={() => jump(1)}
          aria-label="Siguiente"
          className="flex absolute right-1 md:right-2 top-1/2 -translate-y-1/2 z-10 w-9 h-9 md:w-11 md:h-11 items-center justify-center bg-[#1C1C1C]/90 text-[#E9C46A] border border-[#B8933A] hover:bg-[#B8933A] hover:text-white active:bg-[#B8933A] transition-colors shadow-lg"
          data-testid={`${testId}-next`}
        >
          <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
        </button>

        <div
          ref={trackRef}
          onMouseEnter={start}
          onMouseLeave={stop}
          onMouseMove={handleMouseMove}
          className="flex gap-6 overflow-x-auto scroll-smooth px-6 md:px-12 pb-6"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          <style>{`
            [data-testid="${testId}"] .hide-scroll::-webkit-scrollbar { display: none; }
          `}</style>
          {loopItems.map((p, idx) => (
            <article
              key={`${p.id}-${idx}`}
              className="group shrink-0 w-[260px] md:w-[300px] flex flex-col"
              data-testid={`marquee-card-${p.id}`}
            >
              <div className="product-image-wrap">
                <img src={resolveImg(p.image)} alt={p.alt || p.name} loading="lazy" />
              </div>
              <div className="pt-4 space-y-1">
                <span className="eyebrow text-[10px] text-[#B8933A]">{p.brand}</span>
                <h3 className="font-serif-display text-xl text-[#1C1C1C] leading-tight">
                  {p.name}
                </h3>
                <p className="text-xs text-[#595959]">{p.description}</p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="font-serif-display text-lg text-[#1C1C1C] font-semibold">
                    {formatPrice(p.price)}
                  </span>
                </div>
                <button
                  onClick={() => onSelect(p)}
                  className="flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-[#1C1C1C] mt-2 group-hover:gap-3 transition-all"
                  data-testid={`marquee-cta-${p.id}`}
                >
                  {ctaLabel}
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-4">
        <p className="text-xs text-[#9A9A9A] tracking-wide">
          Usa las flechas para avanzar rápido · el carrusel también se mueve solo
        </p>
      </div>
    </section>
  );
}
